import {
  $applyNodeReplacement,
  type DOMExportOutput,
  type EditorConfig,
  LexicalEditor,
  type LexicalNode,
  type SerializedTextNode,
  TextNode,
} from 'lexical';

export type SerializedExtendedTextNode = {
  highlightColor?: string;
  fontColor?: string;
  resolvedHighlightColor?: string;
  resolvedFontColor?: string;
} & SerializedTextNode;

export class ExtendedTextNode extends TextNode {
  __highlightColor: string | undefined;

  __resolvedHighlightColor: string | undefined;

  __fontColor: string | undefined;

  __resolvedFontColor: string | undefined;

  __noDisplayColor: boolean | undefined;

  static getType(): string {
    return 'extended-text';
  }

  static clone(node: ExtendedTextNode): ExtendedTextNode {
    return new ExtendedTextNode(node.__text, node.__key);
  }

  afterCloneFrom(prevNode: this): void {
    super.afterCloneFrom(prevNode);
    this.__highlightColor = prevNode.getHighlightColor();
    this.__fontColor = prevNode.getFontColor();
    this.__resolvedHighlightColor = prevNode.getResolvedHighlightColor();
    this.__resolvedFontColor = prevNode.getResolvedFontColor();
    this.__noDisplayColor = prevNode.getNoDisplayColor();
  }

  static importJSON(serializedNode: SerializedExtendedTextNode): ExtendedTextNode {
    const node = $createExtendedTextNode(serializedNode.text);
    node.setFormat(serializedNode.format);
    node.setDetail(serializedNode.detail);
    node.setMode(serializedNode.mode);
    // Apply dummy styles in addition to actual styles to stop lexical from squashing our nodes
    // together and destroying nodes with styles that need to be hydrated.
    node.setStyle(
      [
        serializedNode.highlightColor && `--highlight-color: ${serializedNode.highlightColor}`,
        serializedNode.fontColor && `--font-color: ${serializedNode.fontColor}`,
        serializedNode.style,
      ]
        .filter(Boolean)
        .join('; '),
    );
    node.setHighlightColor(serializedNode.highlightColor);
    node.setFontColor(serializedNode.fontColor);
    node.setResolvedHighlightColor(serializedNode.resolvedHighlightColor);
    node.setResolvedFontColor(serializedNode.resolvedFontColor);

    return node;
  }

  exportJSON(): SerializedExtendedTextNode {
    return {
      ...super.exportJSON(),
      highlightColor: this.__highlightColor,
      fontColor: this.__fontColor,
      resolvedHighlightColor: this.__resolvedHighlightColor,
      resolvedFontColor: this.__resolvedFontColor,
      type: 'extended-text',
      version: 1,
    };
  }

  createDOM(config: EditorConfig): HTMLElement {
    const dom = super.createDOM(config);

    if (!this.__noDisplayColor) {
      if (this.__resolvedHighlightColor) dom.style.setProperty('background-color', this.__resolvedHighlightColor);
      if (this.__resolvedFontColor) dom.style.setProperty('color', this.__resolvedFontColor);
    }
    dom.classList.add('extended-text-node');
    return dom;
  }

  exportDOM(editor: LexicalEditor): DOMExportOutput {
    const dom = super.exportDOM(editor);
    if (dom.element && 'style' in dom.element) {
      dom.element.style.whiteSpace = '';
    }
    return dom;
  }

  updateDOM(prevNode: ExtendedTextNode, dom: HTMLElement, config: EditorConfig): boolean {
    const isUpdated = super.updateDOM(prevNode, dom, config);
    if (!this.__noDisplayColor) {
      if (dom.style.backgroundColor !== this.__resolvedHighlightColor) {
        dom.style.setProperty('background-color', this.__resolvedHighlightColor ?? '');
      }

      if (dom.style.color !== this.__resolvedFontColor) {
        dom.style.setProperty('color', this.__resolvedFontColor ?? '');
      }
    }
    return isUpdated;
  }

  isTextEntity(): true {
    return true;
  }

  getHighlightColor() {
    const self = this.getLatest();
    return self.__highlightColor;
  }

  setHighlightColor(highlightColor: string | undefined) {
    const writable = this.getWritable();
    writable.__highlightColor = highlightColor;
  }

  getResolvedHighlightColor() {
    const self = this.getLatest();
    return self.__resolvedHighlightColor;
  }

  setResolvedHighlightColor(resolvedHighlightColor: string | undefined) {
    const writable = this.getWritable();
    writable.__resolvedHighlightColor = resolvedHighlightColor;
  }

  getFontColor() {
    const self = this.getLatest();
    return self.__fontColor;
  }

  setFontColor(fontColor: string | undefined) {
    const writable = this.getWritable();
    writable.__fontColor = fontColor;
  }

  getResolvedFontColor() {
    const self = this.getLatest();
    return self.__resolvedFontColor;
  }

  setResolvedFontColor(resolvedFontColor: string | undefined) {
    const writable = this.getWritable();
    writable.__resolvedFontColor = resolvedFontColor;
  }

  getNoDisplayColor() {
    const self = this.getLatest();
    return self.__noDisplayColor;
  }

  setNoDisplayColor(noDisplayColor: boolean | undefined) {
    const writable = this.getWritable();
    if (!writable) {
      console.error('Writable object is undefined');
      return;
    }
    if (writable.__noDisplayColor !== noDisplayColor) {
      writable.__noDisplayColor = noDisplayColor;
    }
  }

  // This exists because otherwise the LexicalTypeAheadPlugin will not work with the ExtendedTextNode,
  // breaking our MentionsPlugin functionality.
  // https://github.com/facebook/lexical/blob/61e17c8489968e821148890a8d132c76e97ef3cc/packages/lexical-react/src/LexicalTypeaheadMenuPlugin.tsx#L45
  isSimpleText(): boolean {
    return this.getType() === 'extended-text';
  }

  splitText(...splitOffsets: Array<number>): Array<TextNode> {
    const self = this.getLatest();

    // When we are replacing TextNodes in lexical with ExtendedTextNodes, this will actually return an array
    // of ExtendedTextNodes.
    const nodes = super.splitText(...splitOffsets);

    // After splitting, all nodes should have the same highlight color and font color
    nodes.forEach((node) => {
      if ($isExtendedTextNode(node)) {
        node.setHighlightColor(self.getHighlightColor());
        node.setFontColor(self.getFontColor());
        node.setResolvedHighlightColor(self.getResolvedHighlightColor());
        node.setResolvedFontColor(self.getResolvedFontColor());
      }
    });
    return nodes;
  }
}

export function $createExtendedTextNode(text: string): ExtendedTextNode {
  const extendedTextNode = new ExtendedTextNode(text);
  return $applyNodeReplacement(extendedTextNode);
}

export function $isExtendedTextNode(node: LexicalNode | null | undefined): node is ExtendedTextNode {
  return node instanceof ExtendedTextNode;
}
